/**
 * Sanitize HTML using a tag whitelist and a strict attribute/link policy.
 *
 * Defaults:
 *  - allowedTags: ["STRONG", "EM", "U", "S", "P", "DIV"]
 *  - allowClass: false
 *  - linkPolicy: "none" (no links are allowed)
 *
 * Link policy:
 *  - "none"     → <a> elements are unwrapped (contents kept, tag removed)
 *  - "internal" → only relative URLs or same-origin absolute URLs are allowed in href
 *  - "all"      → http/https/mailto/tel allowed; external links are hardened with rel="noopener noreferrer"
 *
 * Security notes:
 *  - Disallowed elements are either removed (dangerous ones) or unwrapped (others).
 *  - All attributes are stripped except:
 *      • "class" when allowClass=true (validated against SAFE_CLASS_RE)
 *      • On <a>: "href" (validated), "rel" (sanitized/forced when needed), and "class" (optional)
 *  - Event handlers (on*), style, data-* and src/href on non-anchors are removed.
 *
 * @param input - Raw HTML string to sanitize.
 * @param opts  - Optional configuration to customize behavior.
 * @returns Sanitized HTML string.
 */
export function sanitizeHTML(
    input: string,
    opts?: {
        /** Custom tag whitelist (case-insensitive). */
        allowedTags?: string[];
        /** Allow `class` attribute on elements that pass the tag whitelist. Default: false. */
        allowClass?: boolean;
        /** Link policy: "none" | "internal" | "all". Default: "none". */
        linkPolicy?: "none" | "internal" | "all";
        /**
         * Base origin used to evaluate "internal" links (e.g., "https://example.com").
         * Falls back to window.location.origin when available.
         */
        baseOrigin?: string;
    }
): string {
    // Defaults
    const defaultTags = ["STRONG", "EM", "U", "S", "P", "DIV"];
    const allowClass = !!opts?.allowClass;
    const linkPolicy = opts?.linkPolicy ?? "none";

    // Build allowed tag set (include <a> automatically when links are allowed)
    const tagList = (opts?.allowedTags ?? defaultTags).map((t) => t.toUpperCase());
    if (linkPolicy !== "none" && !tagList.includes("A")) tagList.push("A");
    const ALLOWED_TAGS = new Set(tagList);

    // Constants and helpers
    const SAFE_CLASS_RE = /^[a-zA-Z0-9\-_ ]+$/;
    const DANGEROUS_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META"]);
    const SAFE_HREF_RE = /^(https?:|mailto:|tel:)/i;

    /**
     * Get the base origin used to evaluate same-origin links.
     * Returns null in non-browser environments if not provided.
     */
    const getBaseOrigin = (): string | null => {
        if (opts?.baseOrigin) return opts.baseOrigin.replace(/\/+$/, "");
        try {
            return typeof window !== "undefined" ? window.location.origin : null;
        } catch {
            return null;
        }
    };
    const baseOrigin = getBaseOrigin();

    /** Determine if a URL is relative (no scheme/protocol prefix). */
    const isRelativeUrl = (href: string): boolean => {
        // No "<scheme>://" and no "<scheme>:" prefix
        return !/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(href) && !/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(href);
    };

    /** Determine if a URL resolves to the same origin as baseOrigin. */
    const isSameOrigin = (href: string): boolean => {
        if (!baseOrigin) return isRelativeUrl(href);
        try {
            const url = new URL(href, baseOrigin);
            const base = new URL(baseOrigin);
            return url.origin === base.origin;
        } catch {
            return false;
        }
    };

    /**
     * Replace a node with its children (keeps text/children, removes the element tag itself).
     */
    const unwrapNode = (node: Node): void => {
        const parent = node.parentNode;
        if (!parent) return;
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
    };

    // Parse input HTML into a detached DOM
    const doc = new DOMParser().parseFromString(`<div>${input}</div>`, "text/html");
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) return "";

    // Walk the DOM (elements and text nodes)
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, null);
    const nodes: Node[] = [];
    let n: Node | null = walker.currentNode;
    while (n) {
        nodes.push(n);
        n = walker.nextNode();
    }
    for (const node of nodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = node as HTMLElement;
        const tag = el.tagName;

        // Remove or unwrap disallowed elements
        if (!ALLOWED_TAGS.has(tag)) {
            if (DANGEROUS_TAGS.has(tag)) {
                el.remove();
            } else {
                unwrapNode(el);
            }
            continue;
        }

        // Attribute policy
        if (tag === "A") {
            // Links are only allowed if linkPolicy !== "none"
            if (linkPolicy === "none") {
                unwrapNode(el);
                continue;
            }

            // Validate/sanitize href
            const href = el.getAttribute("href");
            if (!href || !SAFE_HREF_RE.test(href)) {
                // Remove unsafe or missing href
                el.removeAttribute("href");
            } else {
                // Apply internal/external policy
                if (linkPolicy === "internal") {
                    if (!(isRelativeUrl(href) || isSameOrigin(href))) {
                        // External links not allowed under "internal"
                        el.removeAttribute("href");
                    }
                } else if (linkPolicy === "all") {
                    // Harden external links by enforcing rel attributes
                    try {
                        const url = new URL(href, baseOrigin ?? undefined);
                        const isExternal = baseOrigin
                            ? url.origin !== new URL(baseOrigin).origin
                            : !isRelativeUrl(href);

                        if (isExternal) {
                            // Ensure rel contains both noopener and noreferrer
                            const existingRel = (el.getAttribute("rel") || "").toLowerCase();
                            const relParts = new Set(existingRel.split(/\s+/).filter(Boolean));
                            relParts.add("noopener");
                            relParts.add("noreferrer");
                            el.setAttribute("rel", Array.from(relParts).join(" "));
                            el.setAttribute("target", "_blank");
                        }
                    } catch {
                        // Parsing failed → safer to drop href
                        el.removeAttribute("href");
                    }
                }
            }

            // Strip all attributes except href, class (optional), rel
            for (const attr of Array.from(el.attributes)) {
                const name = attr.name.toLowerCase();

                // Always drop dangerous attributes
                if (name.startsWith("on") || name === "style" || name.startsWith("data-")) {
                    el.removeAttribute(attr.name);
                    continue;
                }

                if (name === "href") continue; // already validated above
                if (name === "rel") continue;  // allowed on anchors (may be enforced above)
                if (name === "target") continue; // allow select target

                if (name === "class") {
                    if (!allowClass || !SAFE_CLASS_RE.test(attr.value)) {
                        el.removeAttribute("class");
                    }
                    continue;
                }

                // Drop any other attribute on <a>
                el.removeAttribute(attr.name);
            }

            continue;
        }

        // Non-anchor allowed elements: strip attributes except class (optional)
        for (const attr of Array.from(el.attributes)) {
            const name = attr.name.toLowerCase();

            // Always drop dangerous attributes
            if (
                name.startsWith("on") ||
                name === "style" ||
                name === "href" || // no links on non-anchors
                name === "src"  || // disallow external refs
                name.startsWith("data-")
            ) {
                el.removeAttribute(attr.name);
                continue;
            }

            // Optionally keep class on whitelisted elements
            if (name === "class") {
                if (!allowClass || !SAFE_CLASS_RE.test(attr.value)) {
                    el.removeAttribute("class");
                }
                continue;
            }

            // Remove any other attribute
            el.removeAttribute(attr.name);
        }
    }

    return root.innerHTML;
}
